import React, { useState } from "react";
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonButtons,
  IonMenuButton,
} from "@ionic/react";
import {
  Box,
  TextField,
  MenuItem,
  Paper,
  Select,
  InputLabel,
  FormControl,
  Button,
  Typography,
  CircularProgress,
  Container,
  FormHelperText,
  IconButton,
  FormGroup,
  Collapse,
  Alert,
  AlertTitle,
  Grid,
} from "@mui/material";
import {
  Send as SendIcon,
  Email as EmailIcon,
  Sms as SmsIcon,
  Notifications as NotificationIcon,
  CheckCircle as CheckCircleIcon,
} from "@mui/icons-material";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import axios from "axios";
import { baseUrl, config, getUser, requestID } from "../../common/utils";
import { useBusinessContext } from "../../context/business-context";

// Validation Schema
const validationSchema = yup.object().shape({
  recipient: yup.string().required("Please select a recipient group."),
  message: yup
    .string()
    .required("A message is required.")
    .min(10, "Message must be at least 10 characters long."),
  subject: yup.string().when("messageTypes.email", {
      is: true,
      then: (schema) => schema.required("Subject is required for emails."),
      otherwise: (schema) => schema.optional(),
  }),
  messageTypes: yup.object().shape({
      email: yup.boolean(),
      sms: yup.boolean(),
      notification: yup.boolean(),
    }).test(
      "at-least-one-selected",
      "Please select at least one delivery channel.",
      (value) => !!value.email || !!value.sms || !!value.notification
    ),
});

const Messaging: React.FC = () => {
  const { selectedBusiness } = useBusinessContext();
  const [status, setStatus] = useState({ loading: false, error: false, success: false });
  const [alertMsg, setAlertMsg] = useState("");

  const {
    control,
    handleSubmit,
    watch,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      recipient: "",
      subject: "",
      message: "",
      messageTypes: {
        email: false,
        sms: false,
        notification: false,
      },
    },
  });
  
  const messageTypes = watch("messageTypes");

  const handleSendMessage = async (data: any) => {
    setStatus({ loading: true, error: false, success: false });
    try {
      const formData = new FormData();
      formData.append("requestID", requestID());
      formData.append("business_id", selectedBusiness.business_id);
      formData.append("branch_id", selectedBusiness.branch_id);
      formData.append("recipient", data.recipient);
      formData.append("message", data.message);
      formData.append("subject", data.subject || "");
      formData.append("email", data.messageTypes.email ? "true" : "false");
      formData.append("sms", data.messageTypes.sms ? "true" : "false");
      formData.append("notification", data.messageTypes.notification ? "true" : "false");

      const response = await axios.post(
        baseUrl() + "appmessages/sendMassMessage",
        formData,
        config
      );

      if (response.data.status === "true") {
        setAlertMsg(response.data.message || "Message sent successfully!");
        setStatus({ loading: false, error: false, success: true });
        reset();
      } else {
        setAlertMsg(response.data.message || "An unknown error occurred.");
        setStatus({ loading: false, error: true, success: false });
      }
    } catch (error) {
      console.error("Sending message failed:", error);
      setAlertMsg("Failed to send message. Please check your connection and try again.");
      setStatus({ loading: false, error: true, success: false });
    }
  };

  const businessData = getUser().businessData;

  return (
    <IonPage className="pushDown">
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonMenuButton />
          </IonButtons>
          <IonTitle>Broadcast Messaging</IonTitle>
        </IonToolbar>
      </IonHeader>

      <IonContent fullscreen>
        <Container maxWidth="md" sx={{ py: 4 }}>
          <Paper
            elevation={0}
            sx={{
              p: { xs: 2, sm: 4 },
              borderRadius: 4,
              border: "1px solid #e0e0e0",
              backgroundColor: "white",
            }}
          >
            <Box textAlign="center" mb={4}>
              <Typography variant="h4" component="h1" fontWeight="600">
                Compose a Message
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Reach your users via Email, SMS, or In-App Notification.
              </Typography>
            </Box>
            
            <Collapse in={status.success || status.error}>
              <Alert 
                severity={status.success ? "success" : "error"} 
                sx={{ mb: 3 }}
                onClose={() => setStatus({ loading: false, error: false, success: false })}
              >
                <AlertTitle>{status.success ? "Success" : "Error"}</AlertTitle>
                {alertMsg}
              </Alert>
            </Collapse>

            <form onSubmit={handleSubmit(handleSendMessage)}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Controller
                    name="recipient"
                    control={control}
                    render={({ field }) => (
                      <FormControl fullWidth error={!!errors.recipient}>
                        <InputLabel>Recipient Group</InputLabel>
                        <Select label="Recipient Group" {...field}>
                          <MenuItem value="1">All Users</MenuItem>
                          {Number(businessData.business_type_id) === 1001 && (
                            <MenuItem value="2">Users with Active Loans</MenuItem>
                          )}
                        </Select>
                        <FormHelperText>{errors.recipient?.message}</FormHelperText>
                      </FormControl>
                    )}
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <Typography variant="subtitle1" fontWeight="500" gutterBottom>
                    Delivery Channels
                  </Typography>
                  <FormGroup row>
                    {/* <Controller
                      name="messageTypes.email"
                      control={control}
                      render={({ field }) => (
                        <ChannelButton
                          icon={<EmailIcon />}
                          label="Email"
                          checked={field.value}
                          onChange={(e) => field.onChange(e.target.checked)}
                        />
                      )}
                    /> */}
                    <Controller
                      name="messageTypes.sms"
                      control={control}
                      render={({ field }) => (
                        <ChannelButton
                          icon={<SmsIcon />}
                          label="SMS"
                          checked={field.value}
                          onChange={(e) => field.onChange(e.target.checked)}
                        />
                      )}
                    />
                    <Controller
                      name="messageTypes.notification"
                      control={control}
                      render={({ field }) => (
                        <ChannelButton
                          icon={<NotificationIcon />}
                          label="Notification"
                          checked={field.value}
                          onChange={(e) => field.onChange(e.target.checked)}
                        />
                      )}
                    />
                  </FormGroup>
                  {errors.messageTypes && (
                    <FormHelperText error sx={{ mt: 1 }}>
                        {errors.messageTypes.message}
                    </FormHelperText>
                  )}
                </Grid>

                <Grid item xs={12}>
                  <Collapse in={messageTypes?.email}>
                     <Controller
                        name="subject"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            label="Email Subject"
                            fullWidth
                            error={!!errors.subject}
                            helperText={errors.subject?.message}
                            {...field}
                          />
                        )}
                      />
                  </Collapse>
                </Grid>

                <Grid item xs={12}>
                  <Controller
                    name="message"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        label="Message"
                        multiline
                        rows={8}
                        fullWidth
                        placeholder="Type your message here..."
                        error={!!errors.message}
                        helperText={errors.message?.message}
                        {...field}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    fullWidth
                    disabled={status.loading}
                    startIcon={status.loading ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
                    sx={{ py: 1.5, fontWeight: 600, textTransform: 'none' }}
                  >
                    {status.loading ? "Sending..." : "Send Broadcast"}
                  </Button>
                </Grid>
              </Grid>
            </form>
          </Paper>
        </Container>
      </IonContent>
    </IonPage>
  );
};


// Custom styled button for channel selection
const ChannelButton = ({ icon, label, checked, onChange }: any) => {
  return (
    <Button
      variant={checked ? "contained" : "outlined"}
      onClick={() => onChange({ target: { checked: !checked } })}
      startIcon={icon}
      sx={{
        mr: 2,
        mb: 1,
        textTransform: 'none',
        fontWeight: 500,
        borderRadius: '20px',
        boxShadow: checked ? '0 4px 12px rgba(0,0,0,0.1)' : 'none',
      }}
    >
      {label}
    </Button>
  );
};


export default Messaging;